import React, { useState, useEffect, useRef } from 'react';
import EmptyState from '../../components/ui/EmptyState';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { notificationService } from '../../services/notificationService';
import { announcementService } from '../../services/announcementService';
import { formatRelativeTime, formatDateTime } from '../../utils/formatters';

/**
 * Recipient-facing notifications inbox.
 * Unlike the bell dropdown (latest 10, one-line preview), this shows the full
 * list with complete message bodies, read/unread state, and filtering.
 */
const NotificationsInbox = () => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusId = searchParams?.get('focus');
  const focusRef = useRef(null);
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [marking, setMarking] = useState(false);
  const [expandedPin, setExpandedPin] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
      return;
    }
    loadNotifications();
  }, [user]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const [data, pinnedData] = await Promise.all([
        notificationService?.getUserNotifications(100),
        announcementService?.getPinnedForUser()
      ]);
      setNotifications(data || []);
      setPinned(pinnedData || []);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    setMarking(true);
    try {
      await notificationService?.markAllAsRead();
      setNotifications(prev => prev?.map(n => ({ ...n, isRead: true })));
    } finally {
      setMarking(false);
    }
  };

  const markRead = async (notif) => {
    if (notif?.isRead) return;
    await notificationService?.markAsRead(notif?.id);
    setNotifications(prev => prev?.map(n => n?.id === notif?.id ? { ...n, isRead: true } : n));
  };

  // When arriving from the bell with ?focus=<id>, scroll to and mark that one read.
  useEffect(() => {
    if (loading || !focusId) return;
    const target = notifications?.find(n => n?.id === focusId);
    if (target) markRead(target);
    if (focusRef?.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, focusId]);

  const unreadCount = notifications?.filter(n => !n?.isRead)?.length || 0;
  const visible = filter === 'unread'
    ? notifications?.filter(n => !n?.isRead)
    : notifications;

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {t('notifications.inboxTitle', 'Notifications')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {unreadCount > 0
                ? t('notifications.unreadCount', { count: unreadCount, defaultValue: `${unreadCount} unread` })
                : t('notifications.allCaughtUp', 'You\'re all caught up')}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} disabled={marking} iconName="CheckCheck" iconPosition="left">
              {t('notifications.markAllRead', 'Mark all read')}
            </Button>
          )}
        </div>

        {/* Pinned announcements / FAQ */}
        {!loading && pinned?.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-1.5 mb-2">
              <Icon name="Pin" size={14} className="text-warning" />
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('notifications.pinnedTitle', 'Pinned announcements & FAQ')}
              </h2>
            </div>
            <div className="space-y-2">
              {pinned?.map((a) => {
                const open = expandedPin === a?.id;
                return (
                  <div key={a?.id} className="rounded-lg border border-warning-border bg-warning-subtle/60">
                    <button
                      onClick={() => setExpandedPin(open ? null : a?.id)}
                      className="w-full text-start px-4 py-3 flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{a?.title}</p>
                        {a?.conferenceName && <p className="text-[11px] text-muted-foreground mt-0.5">{a?.conferenceName}</p>}
                      </div>
                      <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                    </button>
                    {open && (
                      <div className="px-4 pb-3 text-sm text-foreground/80 whitespace-pre-wrap break-words border-t border-warning-border pt-2">
                        {a?.body}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('notifications.filterAll', 'All')} ({notifications?.length || 0})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === 'unread' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('notifications.filterUnread', 'Unread')} ({unreadCount})
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="Loader2" size={36} className="text-primary animate-spin" />
          </div>
        ) : visible?.length === 0 ? (
          <div className="bg-card border border-border rounded-lg">
            <EmptyState
              filtered={filter === 'unread'}
              onClear={() => setFilter('all')}
              icon="BellOff"
              title={filter === 'unread'
                ? t('notifications.noUnread', 'No unread notifications')
                : t('notifications.empty', 'No notifications yet')}
              body={filter === 'unread'
                ? t('notifications.noUnreadBody', "You're caught up. Switch to All to see everything you've already read.")
                : t('notifications.emptyBody', 'Review invitations, decisions and deadline reminders arrive here.')}
            />
          </div>
        ) : (
          <div className="space-y-2">
            {visible?.map((notif) => (
              <button
                key={notif?.id}
                ref={notif?.id === focusId ? focusRef : null}
                onClick={() => markRead(notif)}
                className={`w-full text-start p-4 rounded-lg border transition-colors hover:bg-muted/40 ${
                  notif?.id === focusId ? 'ring-2 ring-primary border-primary bg-primary/5'
                    : notif?.isRead ? 'bg-card border-border' : 'bg-primary/5 border-primary/20'
                }`}
                title={formatDateTime(notif?.createdAt)}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1.5 w-2 h-2 flex-shrink-0 ${notif?.isRead ? 'bg-transparent' : 'bg-primary'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-foreground">{notif?.subject}</p>
                      <span className="text-[11px] text-muted-foreground flex-shrink-0">
                        {formatRelativeTime(notif?.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">
                      {notif?.message}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default NotificationsInbox;
